/**
 * OBR-167: control de qualitat a la recepció — la part PURA (sense I/O, sense rellotge). Vegeu
 * docs/Pla_Implementacio_Qualitat_APPCC.md §0 (decisions 2, 3 i 6) i §1 (M3).
 *
 * QUÈ ES CONTROLA. Cada línia que es rep (una fila de `supplier_lots`) porta, a més de la quantitat, el lot i la
 * caducitat, un CONTROL: la temperatura d'arribada, l'estat de l'envàs i el de l'etiquetatge (OK/KO). Quan hi ha
 * alguna cosa que no quadra —un KO o una temperatura fora del rang de la condició de conservació de l'article—
 * qui rep ha de TRIAR UNA DECISIÓ (acceptar, acceptar amb incidència o rebutjar). El servidor fa la resta:
 * obre UNA no conformitat per línia (i, si es rebutja, una devolució pendent que bloqueja l'estoc).
 *
 * REGLES (les mateixes a l'App, al BackOffice i, escrites a mà, al servidor):
 *
 *  1. LA TEMPERATURA ÉS OBLIGATÒRIA quan la condició de conservació de l'article ho demana
 *     (`requires_reception_temperature`). Una condició sense rang no jutja: la temperatura queda «sense
 *     valorar» (`unknown`), mai «fora de rang».
 *  2. EL RANG ÉS TANCAT: justament el mínim o el màxim és dins. Es compara arrodonit a dues xifres, com ho
 *     desa la columna (`numeric(5,2)`), perquè el que decideix el client i el que decideix el servidor no es
 *     separin per un decimal de més.
 *  3. UN KO O UNA TEMPERATURA FORA DE RANG demanen una decisió. Sense res d'això la decisió no té sentit i no
 *     s'envia.
 *  4. ELS AVISOS MAI BLOQUEGEN LA RECEPCIÓ (CLAUDE.md): una temperatura fora de rang no impedeix desar, només
 *     obliga a dir què se'n fa. El que falta per poder confirmar és, doncs, «la temperatura (si és obligatòria)»
 *     i «la decisió (si hi ha alguna cosa que no quadra)», i res més.
 *  5. `reception_control_result` NO es deixa d'omplir (OBR-84): surt DERIVAT del control — `no_conforme` quan hi
 *     ha una incidència o la decisió no és «acceptat», `conforme` quan s'ha controlat alguna cosa i tot és bé.
 *     La marca manual de qui rep (Conforme / No conforme) hi continua comptant: només el pot empitjorar.
 */

import {
  QC_CHECK_RESULTS,
  QC_DECISIONS,
  type QcCheckResult,
  type QcDecision,
  type ReceptionControlResult,
} from '../enums/index.js';
import { parseDecimal, type FormatLocale } from '../format/index.js';

// ═══════════════════════════════════════════════════════════════════════════════════════
// Temperatura
// ═══════════════════════════════════════════════════════════════════════════════════════

/**
 * El marge de temperatures que té sentit anotar en rebre (°C). Una lectura fora d'aquí és un error de tecleig
 * (500) i no es dona per bona; el servidor valida el mateix interval. El mestre de condicions també s'hi limita.
 */
export const RECEPTION_TEMPERATURE_MIN_C = -100;
export const RECEPTION_TEMPERATURE_MAX_C = 100;

/** Decimals de la temperatura: els de la columna `numeric(5,2)`. */
const TEMPERATURE_DECIMALS = 2;
const TEMPERATURE_SCALE = 10 ** TEMPERATURE_DECIMALS;

/** Quantes xifres enteres admet el camp en teclejar (cap lectura sensata en té més de 3). */
const TEMPERATURE_INTEGER_DIGITS = 3;

/** Una temperatura arrodonida a dues xifres (el que desa la base de dades). */
export function roundTemperature(value: number): number {
  const rounded = Math.round(value * TEMPERATURE_SCALE) / TEMPERATURE_SCALE;
  // `-0` es llegiria «-0» a la pantalla i a la base de dades; és un zero.
  return rounded === 0 ? 0 : rounded;
}

/** Una lectura de temperatura vàlida: un nombre finit dins del marge anotable. */
export function isValidTemperature(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= RECEPTION_TEMPERATURE_MIN_C &&
    value <= RECEPTION_TEMPERATURE_MAX_C
  );
}

/**
 * Un nombre que ve d'una base de dades o d'una resposta (un `numeric` que pot arribar com a text) → el nombre
 * finit, o `null` si no ho és. Mai un 0 silenciós: «sense rang» no és «0 °C».
 */
export function coerceTemperature(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * El que el camp de temperatura té escrit → el valor, arrodonit. `null` quan és buit, no es llegeix com a nombre
 * («-», «abc») o cau fora del marge anotable: en cap d'aquests casos hi ha una lectura.
 */
export function parseTemperatureInput(text: string, locale?: FormatLocale): number | null {
  const value = parseDecimal(text, locale);
  if (value === undefined) return null;
  const rounded = roundTemperature(value);
  return isValidTemperature(rounded) ? rounded : null;
}

/** El camp té alguna cosa escrita que no es pot llegir com a temperatura (per pintar-hi l'error). */
export function isTemperatureInputUnreadable(text: string, locale?: FormatLocale): boolean {
  return text.trim() !== '' && parseTemperatureInput(text, locale) === null;
}

/**
 * El que s'accepta MENTRE ES TECLEJA al camp de temperatura: un signe menys al davant (també el «−» tipogràfic),
 * xifres, UN separador decimal (coma o punt, el que s'hagi escrit) i com a molt dues xifres decimals. Tot el
 * demés es descarta, de manera que el camp no pot arribar a tenir un text que no sigui (o no acabi sent) un
 * nombre. «-» sol i «1,» són vàlids: la persona encara està escrivint.
 */
export function normalizeTemperatureInput(text: string): string {
  let sign = '';
  let integer = '';
  let separator = '';
  let fraction = '';
  for (const raw of text.trim()) {
    const ch = raw === '\u2212' ? '-' : raw;
    if (ch === '-') {
      if (integer === '' && separator === '') sign = '-';
    } else if (ch === ',' || ch === '.') {
      if (separator === '') {
        // El camp mai comença per un separador: «,5» és «0,5».
        if (integer === '') integer = '0';
        separator = ch;
      }
    } else if (ch >= '0' && ch <= '9') {
      if (separator !== '') {
        if (fraction.length < TEMPERATURE_DECIMALS) fraction += ch;
      } else if (integer === '0') {
        // «0» seguit d'una xifra és la xifra («05» → «5»), com al teclat de pesatge.
        integer = ch;
      } else if (integer.length < TEMPERATURE_INTEGER_DIGITS) {
        integer += ch;
      }
    }
  }
  return sign + integer + separator + fraction;
}

/** El botó «±» del camp: posa el signe menys al davant si no hi és, i el treu si hi és. */
export function toggleTemperatureSign(text: string): string {
  const trimmed = text.trim();
  return trimmed.startsWith('-') ? trimmed.slice(1) : `-${trimmed}`;
}

/** Com surt una temperatura en relació amb el rang: dins, per sota, per sobre, o no es pot dir. */
export type TemperatureStatus = 'ok' | 'low' | 'high' | 'unknown';

/**
 * Si una temperatura és dins del rang de la condició de conservació.
 *
 * `unknown` quan no hi ha lectura, o quan la condició no té ni mínim ni màxim (no hi ha res amb què
 * comparar-la). Amb només un dels dos límits es jutja només per aquell. Els límits són inclosos.
 */
export function temperatureStatus(
  temperature: number | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined,
): TemperatureStatus {
  if (temperature === null || temperature === undefined || !Number.isFinite(temperature)) {
    return 'unknown';
  }
  const lower = min === null || min === undefined || !Number.isFinite(min) ? null : roundTemperature(min);
  const upper = max === null || max === undefined || !Number.isFinite(max) ? null : roundTemperature(max);
  if (lower === null && upper === null) return 'unknown';
  const value = roundTemperature(temperature);
  if (lower !== null && value < lower) return 'low';
  if (upper !== null && value > upper) return 'high';
  return 'ok';
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// Regles del mestre «Condicions de conservació»
// ═══════════════════════════════════════════════════════════════════════════════════════

/** El que el mestre de condicions de conservació diu de la temperatura de recepció d'un article. */
export interface ReceptionQualityRules {
  /** Mínim acceptable (°C); null = sense mínim. */
  readonly tempMinC: number | null;
  /** Màxim acceptable (°C); null = sense màxim. */
  readonly tempMaxC: number | null;
  /** Cal anotar la temperatura en rebre. */
  readonly requiresTemperature: boolean;
}

/** La condició demana anotar la temperatura en rebre? Un article sense condició no ho demana. */
export function isTemperatureRequired(
  rules: Pick<ReceptionQualityRules, 'requiresTemperature'> | null | undefined,
): boolean {
  return rules?.requiresTemperature === true;
}

/**
 * Les regles d'una fila del mestre tal com arriben del servidor (`numeric` que pot ser text, booleà que pot ser
 * absent). `null` quan no hi ha condició. Una fila sense rang ni obligació és una condició que no jutja res.
 */
export function receptionQualityRules(
  row:
    | {
        readonly temp_min_c?: unknown;
        readonly temp_max_c?: unknown;
        readonly requires_reception_temperature?: unknown;
      }
    | null
    | undefined,
): ReceptionQualityRules | null {
  if (row === null || row === undefined) return null;
  return {
    tempMinC: coerceTemperature(row.temp_min_c),
    tempMaxC: coerceTemperature(row.temp_max_c),
    requiresTemperature: row.requires_reception_temperature === true,
  };
}

/** Un text curt del rang («0 – 4 °C», «≥ 2», «≤ 8»), o null quan no n'hi ha. La unitat la posa qui el pinta. */
export function temperatureRangeParts(
  rules: Pick<ReceptionQualityRules, 'tempMinC' | 'tempMaxC'> | null | undefined,
): { readonly min: number | null; readonly max: number | null } | null {
  if (rules === null || rules === undefined) return null;
  if (rules.tempMinC === null && rules.tempMaxC === null) return null;
  return { min: rules.tempMinC, max: rules.tempMaxC };
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// El que no quadra d'una línia
// ═══════════════════════════════════════════════════════════════════════════════════════

/**
 * Per què una línia no quadra. Els codis són els MATEIXOS que el servidor escriu a `detail.reasons` de la no
 * conformitat (`temperature_out_of_range`, `packaging_ko`, `labeling_ko`): el servidor hi afegeix, a més, els de
 * la decisió.
 */
export const QUALITY_ISSUES = ['temperature_out_of_range', 'packaging_ko', 'labeling_ko'] as const;
export type QualityIssue = (typeof QUALITY_ISSUES)[number];

export interface LineQualityInput {
  readonly temperature: number | null | undefined;
  readonly min: number | null | undefined;
  readonly max: number | null | undefined;
  readonly packaging: QcCheckResult | null | undefined;
  readonly labeling: QcCheckResult | null | undefined;
}

/** Els motius pels quals una línia no quadra, en l'ordre temperatura · envàs · etiquetatge. */
export function lineQualityIssues(input: LineQualityInput): QualityIssue[] {
  const issues: QualityIssue[] = [];
  const status = temperatureStatus(input.temperature, input.min, input.max);
  if (status === 'low' || status === 'high') issues.push('temperature_out_of_range');
  if (input.packaging === 'ko') issues.push('packaging_ko');
  if (input.labeling === 'ko') issues.push('labeling_ko');
  return issues;
}

/** Hi ha alguna cosa que no quadra, i per tant cal triar una decisió. */
export function qualityDecisionRequired(issues: readonly QualityIssue[]): boolean {
  return issues.length > 0;
}

export function isQcCheckResult(value: unknown): value is QcCheckResult {
  return typeof value === 'string' && (QC_CHECK_RESULTS as readonly string[]).includes(value);
}

export function isQcDecision(value: unknown): value is QcDecision {
  return typeof value === 'string' && (QC_DECISIONS as readonly string[]).includes(value);
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// El que falta per poder confirmar
// ═══════════════════════════════════════════════════════════════════════════════════════

/**
 * El que cal omplir d'una línia abans de confirmar: la temperatura (obligatòria, o escrita però no llegible) i
 * la decisió (quan hi ha alguna cosa que no quadra).
 */
export type QualityMissingField = 'temperature' | 'decision';

export interface QualityValidationInput extends LineQualityInput {
  /** La condició de l'article demana anotar la temperatura. */
  readonly requiresTemperature: boolean;
  readonly decision: QcDecision | null | undefined;
  /** Hi ha un text al camp de temperatura que no es pot llegir (cal corregir-lo encara que no sigui obligatori). */
  readonly temperatureUnreadable?: boolean | undefined;
}

/**
 * Què falta d'una línia. Buit = es pot confirmar. Mai cap d'aquests avisos impedeix la recepció del gènere en si
 * (regla 4): és el que la pantalla demana abans de desar el que s'ha vist.
 */
export function missingQualityFields(input: QualityValidationInput): QualityMissingField[] {
  const missing: QualityMissingField[] = [];
  const noReading = input.temperature === null || input.temperature === undefined;
  if (noReading && (input.requiresTemperature || input.temperatureUnreadable === true)) {
    missing.push('temperature');
  }
  const decisionMissing = input.decision === null || input.decision === undefined;
  if (decisionMissing && qualityDecisionRequired(lineQualityIssues(input))) {
    missing.push('decision');
  }
  return missing;
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// La valoració d'una línia (una sola funció, perquè tot arreu es jutgi igual)
// ═══════════════════════════════════════════════════════════════════════════════════════

/** El que se sap del control d'una línia. */
export interface LineQualityFacts {
  readonly temperature: number | null | undefined;
  readonly packaging: QcCheckResult | null | undefined;
  readonly labeling: QcCheckResult | null | undefined;
  /** La decisió triada, tal com es té (pot ser vella: només compta si hi ha alguna cosa que no quadra). */
  readonly decision: QcDecision | null | undefined;
  /** Les regles de la condició de conservació de l'article; null = sense condició. */
  readonly rules: ReceptionQualityRules | null | undefined;
}

export interface LineQualityAssessment {
  readonly issues: readonly QualityIssue[];
  /** Hi ha alguna cosa que no quadra i cal triar decisió. */
  readonly decisionRequired: boolean;
  /** La decisió que es desa: la triada si cal, `null` si no hi ha res que decidir. */
  readonly decision: QcDecision | null;
  /** S'ha controlat alguna cosa de debò (una temperatura dins del rang, o un OK/KO). */
  readonly checked: boolean;
  /** El resultat del control per si sol: `null` quan no s'ha controlat res. */
  readonly verdict: ReceptionControlResult | null;
  /** Estat de la temperatura respecte del rang. */
  readonly temperatureStatus: TemperatureStatus;
}

/**
 * Valora el control d'una línia. La decisió només es conserva quan hi ha alguna cosa que no quadra: si qui rep
 * va triar «rebutja» i després va corregir la temperatura, la decisió vella no es desa. I el RESULTAT
 * (`verdict`): `no_conforme` si hi ha una incidència o la decisió no és «acceptat»; `conforme` si s'ha
 * controlat alguna cosa i tot és bé; `null` si no s'ha controlat res (una temperatura sense rang amb què
 * comparar-la no és un veredicte).
 */
export function assessLineQuality(facts: LineQualityFacts): LineQualityAssessment {
  const rules = facts.rules ?? null;
  const issues = lineQualityIssues({
    temperature: facts.temperature,
    min: rules?.tempMinC ?? null,
    max: rules?.tempMaxC ?? null,
    packaging: facts.packaging,
    labeling: facts.labeling,
  });
  const decisionRequired = qualityDecisionRequired(issues);
  const decision = decisionRequired ? (facts.decision ?? null) : null;
  const status = temperatureStatus(facts.temperature, rules?.tempMinC, rules?.tempMaxC);
  const checked =
    status === 'ok' ||
    (facts.packaging !== null && facts.packaging !== undefined) ||
    (facts.labeling !== null && facts.labeling !== undefined);
  let verdict: ReceptionControlResult | null = null;
  if (issues.length > 0 || (decision !== null && decision !== 'accepted')) verdict = 'no_conforme';
  else if (checked) verdict = 'conforme';
  return { issues, decisionRequired, decision, checked, verdict, temperatureStatus: status };
}

/**
 * El resultat que es desa a `reception_control_result`: el del control i la marca manual de qui rep es
 * combinen sense que cap no pugui amagar una no conformitat. `no_conforme` guanya sempre; després, `conforme`
 * (del control o de la marca); si no hi ha res, `null`.
 */
export function combineControlResult(
  manual: ReceptionControlResult | null | undefined,
  verdict: ReceptionControlResult | null,
): ReceptionControlResult | null {
  if (verdict === 'no_conforme' || manual === 'no_conforme') return 'no_conforme';
  if (verdict === 'conforme') return 'conforme';
  return manual ?? null;
}
